<script>
import axios from '@/utils/axios';
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import ReminderForm from '@/components/common/reminder-form.vue';

export default {
 components: {
  ReminderForm
 }, 
 props: ['stages'], 
 inject: ['setLoading'],  
 data() {
  return {
   is_reminder: false,
   editor: ClassicEditor,
   errors: {}, 
   id: '',
   lead_id: '',
   lead_stage: '',
   description: '',
   noteCreated: false, // Track if note was successfully created
   createdNoteId: null, // Store the created note ID
   isSubmitting: false // Prevent multiple submissions
  };
 },
 methods: {
  addNote(lead) {
   this.lead_id = lead.id;
   this.lead_stage = this.stages.find(stage => stage.id === lead.lead_stage_id);
   // Reset flags when adding new note
   this.noteCreated = false;
   this.createdNoteId = null;
  },

  editNote(lead, note) {
   this.id = note.id;
   this.description = note.description;
   this.lead_id = lead.id;
   // For edit mode, note already exists
   this.noteCreated = true;
   this.createdNoteId = note.id;
  },

  async submitForm(){
  // Prevent multiple submissions
   if(this.isSubmitting){
    return;
   }

   this.errors = {};
   this.isSubmitting = true;
   this.setLoading(true);
  
   try {
    // Only create/update note if it hasn't been created successfully yet
    if(!this.noteCreated){
     let noteResponse;
     if(this.id){
      // Update existing note
      noteResponse = await axios.put(`leads/${this.lead_id}/comments/update`, {
       id: this.id,
       description: this.description  
      });
       this.noteCreated = true;
       this.createdNoteId = this.id;
      }else{
       // Create new note
       noteResponse = await axios.post(`leads/${this.lead_id}/comments/create`, {
        lead_stage_id: this.lead_stage?.id,
        description: this.description  
       });
       this.noteCreated = true;
       this.createdNoteId = noteResponse.data.id || noteResponse.data.note?.id;
      }
     this.$swalToast('Note saved successfully!');
    }

     // Handle reminder submission if enabled
     if(this.is_reminder){
      // Access the ReminderForm component using $refs
      const reminderSuccess = await this.$refs.reminderForm.submitReminder();

      if(reminderSuccess){
       this.$swalToast('Reminder created successfully!');
       this.$emit('updated'); 
       this.$closeModal('notes_modal');
       this.resetForm();
      }else{
       // Note was created but reminder failed - don't close modal
       //this.toast.warning('Note saved! Please fix the reminder errors below and submit again.');
      }
     }else{
      // No reminder needed, complete the process
      this.$emit('updated'); 
      this.$closeModal('notes_modal');
      this.resetForm();
     }
    
    }catch(error){
     if(error.type === 'validation'){
      this.errors = error.errors;
     }else{
      this.$swalToast(error.message || 'An error occurred', 'error'); 
     }
    }finally{
     this.isSubmitting = false;
     this.setLoading(false);
    } 
  },

  // Handle reminder creation success from child component
  handleReminderCreated(reminderData) {
   //console.log('Reminder created:', reminderData);
  },

  // Handle reminder creation error from child component
  handleReminderError(error) {
   //console.log('Reminder error:', error);
  },

  async deleteNote() {
   this.errors = {};
   this.setLoading(true); 
  
   try{
    const response = await axios.delete(`leads/${this.lead_id}/comments/${this.id}/delete`);
    this.$swalToast(response.data.message);
    this.$emit('updated');
    this.$closeModal('note_delete_modal');
   }catch(error){
    if(error.type == 'validation'){
     this.errors = error.errors;
    }else{
     this.$swalToast(error.message, 'error'); 
    }
   }finally{
    this.setLoading(false);
   }
  },

  resetForm() {
   this.id = null;
   this.description = '';
   this.errors = {};
   this.is_reminder = false;
   this.noteCreated = false; // Reset note creation flag
   this.createdNoteId = null; // Reset created note ID
   this.isSubmitting = false; // Reset submission flag
  
   // Reset reminder form if it exists
   if(this.$refs.reminderForm){
    this.$refs.reminderForm.resetForm();
   }
 }
}
};
</script>

<template>
<!-- Add Note -->
<div class="modal fade" id="notes_modal">
<div class="modal-dialog modal-dialog-centered modal-md">
  <div class="modal-content">
    <div class="modal-header">
      <h4 class="modal-title">Add Notes</h4>
      <button type="button" class="btn-close custom-btn-close" data-bs-dismiss="modal" aria-label="Close">
        <i class="ti ti-x"></i>
      </button>
    </div>
    
    <form @submit.prevent="submitForm">
      <div class="modal-body">
        
        <!-- Show success message if note was created -->
        <div v-if="noteCreated" class="alert alert-success alert-dismissible mb-3">
          <i class="ti ti-checks me-2"></i>
          <strong>Note saved successfully!</strong> 
          <p v-if="is_reminder">Please complete the reminder details below.</p>
        </div>

        <div class="row">
         <div class="col-lg-12">
          <div class="mb-0 summer-description-box notes-summernote">
              
           <div class="d-flex align-items-center justify-content-between mb-3">
            <label class="form-label mb-0">Comment</label>
            <vue-select :options="stages" label="name" v-model="lead_stage" />
           </div>

              <ckeditor :editor="editor" v-model="description" :disabled="noteCreated"></ckeditor>
              <div v-if="errors.description" class="text-danger small mt-1">{{ errors.description[0] }}</div>
              <small>Maximum 255 Characters</small>
            </div>
          </div>
        </div>

        <div class="form-check form-check-inline form-checkbox-success mb-2">
          <input type="checkbox" class="form-check-input me-2" name="is_reminder" id="is_reminder" v-model="is_reminder" :disabled="noteCreated && !is_reminder">
          <label class="form-check-label" for="is_reminder">Create Reminder?</label>
        </div>
        
        <div v-if="is_reminder">
          <hr>
          <h5>Reminder Details</h5>
          <ReminderForm ref="reminderForm" :lead-id="lead_id" @reminder-created="handleReminderCreated" @reminder-error="handleReminderError"/>
        </div>
      </div>
      
      <div class="modal-footer">
        <button type="button" class="btn btn-light me-2" data-bs-dismiss="modal">Cancel</button>
        <button type="submit" class="btn btn-primary" :disabled="isSubmitting">
          <span v-if="isSubmitting">
            <span class="spinner-border spinner-border-sm me-2" role="status"></span>
            Submitting...
          </span>
          <span v-else-if="noteCreated && is_reminder">
            Create Reminder
          </span>
          <span v-else>
            Submit
          </span>
        </button>
      </div>
    </form>
  </div>
</div>
</div>
<!-- /Add Note -->

<!-- Delete Modal remains the same -->
<div class="modal fade" id="note_delete_modal">
<div class="modal-dialog modal-dialog-centered">
  <div class="modal-content">
    <div class="modal-body text-center">
      <span class="avatar avatar-xl bg-transparent-danger text-danger mb-3">
        <i class="ti ti-trash-x fs-36"></i>
      </span>
      <h4 class="mb-1">Confirm Delete</h4>
      <p class="mb-3">
        You want to delete all the marked items, this cant be undone once you delete.
      </p>
      <div class="d-flex justify-content-center">
        <a href="javascript:void(0);" class="btn btn-light me-3" data-bs-dismiss="modal">Cancel</a>
        <a href="javascript:void(0);" class="btn btn-danger" @click="deleteNote">Yes, Delete</a>
      </div>
    </div>
  </div>
</div>
</div>
<!-- /Delete Modal -->
</template>
